import Input from "./Input/Input";
import type { InputProps } from "./Input/Input";
import Password from "./Password/Password";
import type { PasswordProps } from "./Password/Password";
import Select from "./Select/Select";
import type { SelectProps } from "./Select/Select";
import FileInput from "./FileInput/FileInput";
import type { FileInputProps } from "./FileInput/FileInput";
import Checkbox from "./Checkbox/Checkbox";
import type { CheckBoxProps } from "./Checkbox/Checkbox";
import FieldArray from "./FieldArray/FieldArray";
import type { FieldArrayProps } from "./FieldArray/FieldArray";
import Range from "./Range/Range";
import type { RangeProps } from "./Range/Range";
import TextArea from "./TextArea/TextArea";
import type { TextAreaProps } from "./TextArea/TextArea";
import Markdown from "./MarkdownEditor/Markdown";
import type { MarkdownProps } from "./MarkdownEditor/Markdown";
import DateComponent from "./Date/Date";
import type { DateInputProps } from "./Date/Date";
import Time from "./Time/Time";
import type { TimeInputProps } from "./Time/Time";
import DateTime from "./DateTime/DateTime";
import type { DateTimeInputProps } from "./DateTime/DateTime";

export type FormControlType =
  | "input"
  | "password"
  | "select"
  | "file"
  | "checkbox"
  | "fieldArray"
  | "range"
  | "markdown"
  | "textarea"
  | "date"
  | "time"
  | "datetime";

/**
 * Discriminated union — `controlType` picks which control (and which of its
 * props) is valid for a given usage.
 */
type FormControlProps =
  | ({ controlType: "input" } & InputProps)
  | ({ controlType: "password" } & PasswordProps)
  | ({ controlType: "select" } & SelectProps)
  | ({ controlType: "file" } & FileInputProps)
  | ({ controlType: "checkbox" } & CheckBoxProps)
  | ({ controlType: "fieldArray" } & FieldArrayProps)
  | ({ controlType: "range" } & RangeProps)
  | ({ controlType: "markdown" } & MarkdownProps)
  | ({ controlType: "textarea" } & TextAreaProps)
  | ({ controlType: "date" } & DateInputProps)
  | ({ controlType: "time" } & TimeInputProps)
  | ({ controlType: "datetime" } & DateTimeInputProps);

const FormControl = (props: FormControlProps) => {
  switch (props.controlType) {
    case "input": {
      const { controlType: _controlType, ...rest } = props;
      return <Input {...rest} />;
    }

    case "password": {
      const { controlType: _controlType, ...rest } = props;
      return <Password {...rest} />;
    }

    case "select": {
      const { controlType: _controlType, ...rest } = props;
      return <Select {...rest} />;
    }

    case "file": {
      const { controlType: _controlType, ...rest } = props;
      return <FileInput {...rest} />;
    }

    case "checkbox": {
      return <Checkbox {...props} />;
    }

    case "fieldArray": {
      const { controlType: _controlType, ...rest } = props;
      return <FieldArray {...rest} />;
    }

    case "range": {
      const { controlType: _controlType, ...rest } = props;
      return <Range {...rest} />;
    }

    case "markdown": {
      const { controlType: _controlType, ...rest } = props;
      return <Markdown {...rest} />;
    }

    case "textarea": {
      const { controlType: _controlType, ...rest } = props;
      return <TextArea {...rest} />;
    }

    case "date": {
      const { controlType: _controlType, ...rest } = props;
      return <DateComponent {...rest} />;
    }

    case "time": {
      const { controlType: _controlType, ...rest } = props;
      return <Time {...rest} />;
    }

    case "datetime": {
      const { controlType: _controlType, ...rest } = props;
      return <DateTime {...rest} />;
    }

    default:
      return null;
  }
};

export default FormControl;
